import { useCallback, useEffect, useMemo } from 'react';

import { gql, useQuery } from '@zenbusiness/application-commons-graphql';
import useLocalStorage from '@zenbusiness/application-commons-hooks/useLocalStorage';

import { DB_APPLICATION_STATUS } from '#client/constants/application';
import useApplicationStatusContext from '#client/hooks/useApplicationStatusContext';
import useAstraProfileContext from '#client/hooks/useAstraProfileContext';
import useConstBusinessEntityUuid from '#client/hooks/useConstBusinessEntityUuid';
import { useLogError } from '#client/hooks/useLogError';
import useToastContext from '#client/hooks/useToastContext';
import useUnitAccountId from '#client/hooks/useUnitAccountId';

export const GET_TRANSACTION_DATA_QUERY = gql`
  query transactionsData(
    $businessEntityUuid: UUID!
    $unitAccountId: NumericID!
    $skipTransactions: Boolean!
    $skipPayments: Boolean!
    $skipCheckDeposits: Boolean!
    $skipDebitCardPayment: Boolean!
  ) {
    transactions(
      params: {
        filter: { businessEntityUuid: $businessEntityUuid }
        pagination: { limit: 1, offset: 0 }
      }
    ) @skip(if: $skipTransactions) {
      data {
        id
      }
    }
    bankingPayments(
      filter: { businessEntityUuid: $businessEntityUuid }
      page: { pageSize: 1, page: 1 }
    ) @skip(if: $skipPayments) {
      bankingPayments {
        id
      }
    }
    checkDeposits(
      params: {
        identifier: { businessUuid: $businessEntityUuid, unitAccountId: $unitAccountId }
        filter: {}
        pagination: { limit: 1, offset: 0 }
      }
    ) @skip(if: $skipCheckDeposits) {
      checkDeposits {
        id
        type
        depositTo
        status
        summary
        amount {
          amount
          currency
          formattedAmount
        }
        createdAt
        estimatedCompletionDate
        transactionId
        reason
        method
      }
      pagination {
        limit
        offset
        next
        total
      }
    }
    instantFundingTransferRoutines(input: { businessEntityUuid: $businessEntityUuid })
      @skip(if: $skipDebitCardPayment) {
      data {
        uuid
        routineStatus
        amount {
          amount
          currency
          formattedAmount
        }
        createdDatetime
        updatedDatetime
      }
    }
  }
`;

export default function useCheckTransaction() {
  const { businessEntityUuid } = useConstBusinessEntityUuid();
  const { appStatus, loading: appStatusLoading } = useApplicationStatusContext();
  const { createCommonErrorToast } = useToastContext();
  const { isExistedAstraProfile, loading: astraProfileLoading } = useAstraProfileContext();

  const {
    unitAccountId,
    isValidMfa,
    isEnabled,
    isEnrolled,
    isFetchingMfaStatus,
    loading: unitAccountIdLoading
  } = useUnitAccountId();

  /**
   * Store the has_transaction in the local storage to prevent API call
   */
  const [hasTransactionCache, setHasTransactionCache] = useLocalStorage(
    `has_transaction.${businessEntityUuid}`,
    false
  );
  const [hasPaymentCache, setHasPaymentCache] = useLocalStorage(
    `has_payment.${businessEntityUuid}`,
    false
  );
  const [hasCheckDepositCache, setHasCheckDepositCache] = useLocalStorage(
    `has_check_deposit.${businessEntityUuid}`,
    false
  );
  const [hasDebitCardPaymentCache, setHasDebitCardPaymentCache] = useLocalStorage(
    `has_debit_card_payment.${businessEntityUuid}`,
    false
  );
  // const hasTransaction = hasTransactionCache === true;
  const isSkipQueryCommon =
    appStatusLoading ||
    (appStatus && appStatus === DB_APPLICATION_STATUS.CLOSED) ||
    isFetchingMfaStatus ||
    !isEnrolled ||
    !isEnabled ||
    !isValidMfa ||
    !unitAccountId;

  const isSkipQueryTransaction = isSkipQueryCommon || hasTransactionCache;
  const isSkipQueryPayment = isSkipQueryCommon || hasPaymentCache;
  const isSkipQueryCheckDeposit = isSkipQueryCommon || hasCheckDepositCache;
  const isSkipQueryDebitCardPayment =
    isSkipQueryCommon || hasDebitCardPaymentCache || !isExistedAstraProfile;

  const {
    data: transactionData,
    loading: transactionLoading,
    error,
    errors,
    refetch: refetchTransactions
  } = useQuery(GET_TRANSACTION_DATA_QUERY, {
    skip: !unitAccountId,
    variables: {
      businessEntityUuid,
      unitAccountId,
      skipTransactions: isSkipQueryTransaction,
      skipPayments: isSkipQueryPayment,
      skipCheckDeposits: isSkipQueryCheckDeposit,
      skipDebitCardPayment: isSkipQueryDebitCardPayment
    },
    noThrow: true
  });

  useLogError(errors, error);

  useEffect(() => {
    if (error) {
      createCommonErrorToast({ error });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [error]);

  const loading = unitAccountIdLoading || transactionLoading || astraProfileLoading;

  const numberOfTransactions = transactionData?.transactions?.data?.length;
  const numberOfPayments = transactionData?.bankingPayments?.bankingPayments?.length;
  const numberOfCheckDeposits = transactionData?.checkDeposits?.checkDeposits?.length;
  const numberOfDebitCardPayments =
    transactionData?.instantFundingTransferRoutines?.data?.length ?? 0;

  // has counterparty + NO payments/transactions record => Fund your account
  const isEmptyTransaction =
    !hasTransactionCache &&
    !hasPaymentCache &&
    !hasCheckDepositCache &&
    !hasDebitCardPaymentCache &&
    numberOfTransactions === 0 &&
    numberOfPayments === 0 &&
    numberOfCheckDeposits === 0 &&
    numberOfDebitCardPayments === 0;

  const hasTransactionOrPayments =
    numberOfTransactions > 0 ||
    numberOfPayments > 0 ||
    numberOfCheckDeposits > 0 ||
    numberOfDebitCardPayments > 0 ||
    hasTransactionCache ||
    hasPaymentCache ||
    hasCheckDepositCache ||
    hasDebitCardPaymentCache;

  const hasTransaction = numberOfTransactions > 0 || hasTransactionCache;

  useEffect(() => {
    const checkHasTransaction = numberOfTransactions > 0;
    if (checkHasTransaction) {
      setHasTransactionCache(true);
    }

    const checkHasPayments = numberOfPayments > 0;
    if (checkHasPayments) {
      setHasPaymentCache(true);
    }

    const checkHasCheckDeposit = numberOfCheckDeposits > 0;
    if (checkHasCheckDeposit) {
      setHasCheckDepositCache(true);
    }

    const checkHasDebitCardPayment = numberOfDebitCardPayments > 0;
    if (checkHasDebitCardPayment) {
      setHasDebitCardPaymentCache(true);
    }
  }, [
    isEmptyTransaction,
    numberOfPayments,
    numberOfTransactions,
    numberOfCheckDeposits,
    setHasTransactionCache,
    setHasPaymentCache,
    setHasCheckDepositCache,
    numberOfDebitCardPayments,
    setHasDebitCardPaymentCache
  ]);

  const refetchForFirstCheckDeposit = useCallback(() => {
    if (numberOfCheckDeposits === 0) {
      refetchTransactions();
    }
  }, [numberOfCheckDeposits, refetchTransactions]);

  const refetchForFirstPayment = useCallback(() => {
    if (numberOfPayments === 0) {
      refetchTransactions();
    }
  }, [numberOfPayments, refetchTransactions]);

  const refetchForFirstTransaction = useCallback(() => {
    if (numberOfTransactions === 0) {
      refetchTransactions();
    }
  }, [numberOfTransactions, refetchTransactions]);

  const refetchForDebitCardPayment = useCallback(() => {
    if (numberOfDebitCardPayments === 0) {
      refetchTransactions();
    }
  }, [numberOfDebitCardPayments, refetchTransactions]);

  const value = useMemo(
    () => ({
      error,
      loading,
      isEmptyTransaction,
      hasTransactionOrPayments,
      hasTransaction,
      refetchForFirstCheckDeposit,
      refetchForFirstPayment,
      refetchForFirstTransaction,
      refetchForDebitCardPayment
    }),
    [
      error,
      loading,
      isEmptyTransaction,
      hasTransactionOrPayments,
      hasTransaction,
      refetchForFirstCheckDeposit,
      refetchForFirstPayment,
      refetchForFirstTransaction,
      refetchForDebitCardPayment
    ]
  );

  return value;
}
